import type { MarketplaceVenue } from "@/app/components/marketplace/MarketplaceVenueCard";
import type { MarketplaceVenueImage } from "@/app/components/marketplace/MarketplaceVenueMediaCarousel";
import {
  buildLocationGiftCardsPath,
  getBusinessGiftCardCatalog,
} from "@/app/commerce/giftCards/businessGiftCardCatalog";
import {
  buildLocationMembershipsPath,
  getMembershipsForLocation,
  resolveMembershipOnlinePurchase,
} from "@/app/commerce/memberships/membershipCatalog";
import { validateSpecialistPackageOnlinePurchase } from "@/app/commerce/packages/packagePurchaseCatalog";
import { VENUE_RESULTS_FIXTURES } from "@/app/results/resultsFixtures";
import type {
  ResultsAmenity,
  ResultsAudience,
  ResultsBadge,
  ResultsCoordinates,
  ResultsPrice,
  VenueResult,
} from "@/app/results/resultsTypes";
import { resolveLocationAppointmentCatalog } from "@/app/booking/adapters/locationAppointmentCatalog";
import {
  SPECIALIST_LOCATION_TEAM_ASSIGNMENTS,
  SPECIALIST_ASSIGNMENTS,
  getSpecialistPerson,
  getSpecialistWorkplace,
} from "@/app/specialists/catalog";
import type {
  SpecialistAssignment,
  SpecialistLocationTeamAssignment,
  SpecialistPackage,
  SpecialistPerson,
  SpecialistService,
  SpecialistServiceOffering,
  SpecialistWorkplace,
} from "@/app/specialists/model";
import { getSpecialistPackagesForAssignment } from "@/app/specialists/packages";
import { getSpecialistProfilePortfolioItems } from "@/app/specialists/profilePortfolioFixtures";
import { buildSpecialistProfilePath } from "@/app/specialists/routes";

import { LOCATION_PROFILE_PORTFOLIO_MAX_ITEMS } from "./locationPortfolioPolicy";
import { buildLocationProfilePath, buildLocationServicesHref } from "./routes";

export const LOCATION_PROFILE_SCHEMA_VERSION = 1 as const;
export const LOCATION_PROFILE_PROVIDER_ID =
  "frontend-marketplace-location-profile" as const;
export const LOCATION_PROFILE_PROVIDER_REVISION =
  "frontend-marketplace-location-profile-2026-08-01" as const;
export const LOCATION_PROFILE_SNAPSHOT_SCHEMA_VERSION = 2 as const;
export const LOCATION_PROFILE_SNAPSHOT_PROVIDER_REVISION =
  "frontend-marketplace-location-profile-snapshot-2026-08-07" as const;
export const LOCATION_PROFILE_BUSINESS_PROVIDER_ID =
  "frontend-fixture-location-business-profile" as const;
export const LOCATION_PROFILE_PORTFOLIO_PROVIDER_ID =
  "frontend-fixture-location-portfolio" as const;
export const LOCATION_PROFILE_PORTFOLIO_SCHEMA_VERSION = 2 as const;
export const LOCATION_PROFILE_REVIEW_PROVIDER_ID =
  "frontend-fixture-location-reviews" as const;
export const LOCATION_PROFILE_NEARBY_PROVIDER_ID =
  "frontend-fixture-location-nearby" as const;
export { LOCATION_PROFILE_PORTFOLIO_MAX_ITEMS };
export const LOCATION_PROFILE_ABOUT_MAX_LENGTH = 1_200;
export const LOCATION_PROFILE_SERVICE_PREVIEW_LIMITS = Object.freeze({
  expanded: 4,
  responsive: 4,
});
export const LOCATION_PROFILE_REVIEW_PREVIEW_LIMITS = Object.freeze({
  expanded: 4,
  responsive: 3,
});

export type LocationProfileWeekday =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

const LOCATION_PROFILE_WEEKDAYS: readonly LocationProfileWeekday[] =
  Object.freeze([
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ]);

export interface LocationProfileLocalizedText {
  en: string;
  ro: string;
}

export interface LocationProfileOpeningInterval {
  opensAt: string;
  closesAt: string;
}

export interface LocationProfileWeeklyHoursEntry {
  day: LocationProfileWeekday;
  intervals: readonly LocationProfileOpeningInterval[];
}

export interface LocationProfileAdditionalInformationEntry {
  informationId: string;
  title: LocationProfileLocalizedText;
  description: LocationProfileLocalizedText;
}

export interface LocationProfileContactEvidence {
  phone: string | null;
  email: string | null;
  websiteHref: string | null;
}

export interface LocationProfileBusinessEvidenceV1 {
  schemaVersion: 1;
  providerId: string;
  providerRevision: string;
  locationId: string;
  timeZone: string;
  about: LocationProfileLocalizedText;
  weeklyHours: readonly LocationProfileWeeklyHoursEntry[];
  additionalInformation: readonly LocationProfileAdditionalInformationEntry[];
  contact: LocationProfileContactEvidence | null;
  directionsHref: string | null;
}

/**
 * Explicit business-authored presentation evidence. It is intentionally
 * independent from availability: opening hours cannot be inferred from slots,
 * and missing evidence stays absent in the public snapshot.
 */
export const LOCATION_PROFILE_BUSINESS_EVIDENCE: readonly LocationProfileBusinessEvidenceV1[] =
  cloneDeepFrozen<LocationProfileBusinessEvidenceV1[]>([
    Object.freeze({
      schemaVersion: 1,
      providerId: LOCATION_PROFILE_BUSINESS_PROVIDER_ID,
      providerRevision: "glow-room-business-profile-about-2026-08-03",
      locationId: "venue-glow-room",
      timeZone: "Europe/Bucharest",
      about: Object.freeze({
        en: [
          "Glow Room is a beauty studio in central Timișoara, created for clients who want carefully planned make-up, brow and lash services in a calm, welcoming setting.",
          "Our team combines professional technique with an attentive consultation so every look is adapted to your features, personal style, event and preferred level of intensity.",
          "Appointments may include skin preparation, colour guidance, precise shaping and long-wear finishing, with every stage explained clearly before we continue.",
          "The studio is arranged to offer comfort, privacy and enough time for thoughtful work, whether you are preparing for an important celebration or simply refreshing your everyday look.",
          "We select products and methods according to the requested result and discuss relevant preferences in advance, including finish, coverage, tones and maintenance.",
          "For the best experience, please arrive a few minutes before your booking and let us know about sensitivities or any details that could help us personalise the service.",
          "Every appointment is delivered by a published member of the Glow Room team, with the same focus on cleanliness, consistency and a result that feels naturally yours.",
        ].join(" "),
        ro: [
          "Glow Room este un studio de frumusețe din centrul Timișoarei, creat pentru clientele care își doresc servicii de machiaj, sprâncene și gene atent pregătite, într-un spațiu calm și primitor.",
          "Echipa noastră combină tehnica profesională cu o consultație atentă, astfel încât fiecare rezultat să fie adaptat trăsăturilor, stilului personal, evenimentului și intensității preferate.",
          "Programarea poate include pregătirea tenului, alegerea cromatică, definirea precisă și finisarea de lungă durată, iar fiecare etapă este explicată clar înainte de a continua.",
          "Studioul oferă confort, intimitate și timp pentru fiecare detaliu, fie că te pregătești pentru un eveniment sau îți dorești un aspect proaspăt de zi cu zi.",
          "Selectăm produsele și tehnicile în funcție de rezultatul dorit și discutăm din timp preferințele relevante, inclusiv finisajul, acoperirea, nuanțele și întreținerea.",
          "Pentru cea mai bună experiență, te rugăm să ajungi puțin mai devreme și să ne comunici sensibilitățile sau detaliile utile personalizării serviciului.",
          "Fiecare programare este realizată de un membru publicat al echipei Glow Room, cu aceeași atenție pentru curățenie, consecvență și un rezultat care să te reprezinte natural.",
        ].join(" "),
      }),
      weeklyHours: Object.freeze<LocationProfileWeeklyHoursEntry[]>([
        { day: "monday", intervals: [{ opensAt: "09:00", closesAt: "20:00" }] },
        {
          day: "tuesday",
          intervals: [{ opensAt: "09:00", closesAt: "20:00" }],
        },
        {
          day: "wednesday",
          intervals: [{ opensAt: "09:00", closesAt: "20:00" }],
        },
        {
          day: "thursday",
          intervals: [{ opensAt: "09:00", closesAt: "20:00" }],
        },
        { day: "friday", intervals: [{ opensAt: "09:00", closesAt: "20:00" }] },
        {
          day: "saturday",
          intervals: [{ opensAt: "10:00", closesAt: "18:00" }],
        },
        { day: "sunday", intervals: [] },
      ]),
      additionalInformation: Object.freeze<
        LocationProfileAdditionalInformationEntry[]
      >([
        Object.freeze({
          informationId: "arrival",
          title: Object.freeze({
            en: "Before your visit",
            ro: "Înainte de vizită",
          }),
          description: Object.freeze({
            en: "Please arrive five minutes before your appointment. If you need to change your booking, use the options available in Activity.",
            ro: "Te rugăm să ajungi cu cinci minute înaintea programării. Dacă trebuie să modifici rezervarea, folosește opțiunile disponibile în Activitate.",
          }),
        }),
      ]),
      contact: null,
      directionsHref:
        "https://www.google.com/maps/search/?api=1&query=Glow%20Room%2C%20Strada%20Eugeniu%20de%20Savoya%2012%2C%20Timisoara",
    }),
  ]);

export interface LocationProfileProviderLineage {
  providerId: string;
  providerRevision: string;
}

export interface LocationProfilePortfolioContributor {
  personId: string;
  displayName: string;
}

/** Provider evidence for one venue-owned service-result image. */
export interface LocationProfileServicePortfolioEvidenceItemV2 {
  sourceKind: "service";
  itemId: string;
  locationId: string;
  serviceTag: {
    catalogVersion: string;
    serviceId: string;
  } | null;
  src: string;
  alt: LocationProfileLocalizedText;
  caption: LocationProfileLocalizedText | null;
}

/** Explicit publication reference to one professional Team portfolio image. */
export interface LocationProfileTeamPortfolioEvidenceItemV2 {
  sourceKind: "team";
  itemId: string;
  locationId: string;
  contributor: LocationProfilePortfolioContributor;
  professionalPortfolioItemId: string;
  alt: LocationProfileLocalizedText;
  caption: LocationProfileLocalizedText | null;
}

export type LocationProfilePortfolioEvidenceItemV2 =
  | LocationProfileServicePortfolioEvidenceItemV2
  | LocationProfileTeamPortfolioEvidenceItemV2;

export interface LocationProfilePortfolioServiceReference {
  catalogVersion: string;
  serviceId: string;
  serviceName: string;
  categoryId: string | null;
  bookingHref: string;
}

export type LocationProfilePortfolioSource =
  | {
      kind: "service";
      service: LocationProfilePortfolioServiceReference | null;
    }
  | {
      kind: "team";
      contributor: LocationProfilePortfolioContributor;
      professionalPortfolioItemId: string;
    };

/** Resolved public row shared by the Location document and both viewers. */
export interface LocationProfilePortfolioItemV2 {
  itemId: string;
  locationId: string;
  source: LocationProfilePortfolioSource;
  src: string;
  alt: LocationProfileLocalizedText;
  caption: LocationProfileLocalizedText | null;
}

export interface LocationProfilePortfolioEvidenceV2 {
  schemaVersion: typeof LOCATION_PROFILE_PORTFOLIO_SCHEMA_VERSION;
  providerId: string;
  providerRevision: string;
  locationId: string;
  items: readonly LocationProfilePortfolioEvidenceItemV2[];
}

const THIRD_GENERATION_SERVICE_PORTFOLIO_CATALOG_VERSION =
  "specialist-fixture-2026-07-25:venue-3rd-generation";
const SERVICE_PORTFOLIO_UNSPLASH_PARAMS =
  "?auto=format&fit=crop&w=1600&q=82";

function thirdGenerationServicePortfolioItem(input: {
  alt: LocationProfileLocalizedText;
  caption: LocationProfileLocalizedText;
  itemId: string;
  photoId: string;
  serviceId: string;
}): LocationProfileServicePortfolioEvidenceItemV2 {
  return {
    sourceKind: "service",
    itemId: input.itemId,
    locationId: "venue-3rd-generation",
    serviceTag: {
      catalogVersion: THIRD_GENERATION_SERVICE_PORTFOLIO_CATALOG_VERSION,
      serviceId: input.serviceId,
    },
    src: `https://images.unsplash.com/photo-${input.photoId}${SERVICE_PORTFOLIO_UNSPLASH_PARAMS}`,
    alt: input.alt,
    caption: input.caption,
  };
}

/**
 * Location-owned publication evidence. Venue hero images are never a fallback.
 * Team media is referenced explicitly and service media carries an exact,
 * nullable catalog tag, matching Fresha's Team/Service Portfolio distinction.
 */
export const LOCATION_PROFILE_PORTFOLIO_EVIDENCE: readonly LocationProfilePortfolioEvidenceV2[] =
  cloneDeepFrozen<LocationProfilePortfolioEvidenceV2[]>([
    {
      schemaVersion: LOCATION_PROFILE_PORTFOLIO_SCHEMA_VERSION,
      providerId: LOCATION_PROFILE_PORTFOLIO_PROVIDER_ID,
      providerRevision: "glow-room-marketplace-portfolio-2026-08-07",
      locationId: "venue-glow-room",
      items: [
        {
          sourceKind: "service",
          itemId: "glow-room-service-portfolio-evening-makeup",
          locationId: "venue-glow-room",
          serviceTag: {
            catalogVersion:
              "specialist-fixture-2026-07-25:venue-glow-room",
            serviceId: "andrada-evening-makeup",
          },
          src: "https://images.unsplash.com/photo-1487412912498-0447578fcca8?auto=format&fit=crop&w=1600&q=82",
          alt: {
            en: "Finished evening makeup result created at Glow Room",
            ro: "Rezultat final de machiaj de seară realizat la Glow Room",
          },
          caption: {
            en: "Evening makeup result",
            ro: "Rezultat machiaj de seară",
          },
        },
        {
          sourceKind: "service",
          itemId: "glow-room-service-portfolio-untagged",
          locationId: "venue-glow-room",
          serviceTag: null,
          src: "https://images.unsplash.com/photo-1612817288484-6f916006741a?auto=format&fit=crop&w=1600&q=82",
          alt: {
            en: "Beauty products used for work published by Glow Room",
            ro: "Produse de frumusețe folosite pentru lucrările publicate de Glow Room",
          },
          caption: null,
        },
        ...Array.from({ length: 8 }, (_, index) => ({
          sourceKind: "team" as const,
          itemId: `glow-room-team-portfolio-${index + 1}`,
          locationId: "venue-glow-room",
          contributor: {
            personId: "specialist-andrada-ivan",
            displayName: "Andrada Ivan",
          },
          professionalPortfolioItemId: `andrada-portfolio-${String(
            index + 1,
          ).padStart(2, "0")}`,
          alt: {
            en: `Professional beauty work published by Andrada Ivan at Glow Room, image ${index + 1}`,
            ro: `Lucrare profesională publicată de Andrada Ivan la Glow Room, imaginea ${index + 1}`,
          },
          caption: null,
        })),
      ],
    },
    {
      schemaVersion: LOCATION_PROFILE_PORTFOLIO_SCHEMA_VERSION,
      providerId: LOCATION_PROFILE_PORTFOLIO_PROVIDER_ID,
      providerRevision: "3rd-generation-service-portfolio-2026-08-26",
      locationId: "venue-3rd-generation",
      items: [
        thirdGenerationServicePortfolioItem({
          itemId: "third-generation-classic-cut-01",
          serviceId: "radu-classic-cut",
          photoId: "1622286342621-4bd786c2447c",
          alt: {
            en: "Classic haircut result with a clean tapered finish",
            ro: "Rezultat de tunsoare clasică cu finisaj curat și estompat",
          },
          caption: {
            en: "Classic cut finish",
            ro: "Finisaj tunsoare clasică",
          },
        }),
        thirdGenerationServicePortfolioItem({
          itemId: "third-generation-classic-cut-02",
          serviceId: "radu-classic-cut",
          photoId: "1517832606299-7ae9b720a186",
          alt: {
            en: "Detailed classic haircut styled at 3rd Generation",
            ro: "Tunsoare clasică atent stilizată la 3rd Generation",
          },
          caption: {
            en: "Classic haircut detail",
            ro: "Detaliu tunsoare clasică",
          },
        }),
        thirdGenerationServicePortfolioItem({
          itemId: "third-generation-skin-fade-01",
          serviceId: "radu-skin-fade",
          photoId: "1541533848490-bc8115cd6522",
          alt: {
            en: "Skin fade result viewed from the side",
            ro: "Rezultat skin fade văzut din lateral",
          },
          caption: {
            en: "Skin fade profile",
            ro: "Profil skin fade",
          },
        }),
        thirdGenerationServicePortfolioItem({
          itemId: "third-generation-skin-fade-02",
          serviceId: "radu-skin-fade",
          photoId: "1511920771146-1a7271092231",
          alt: {
            en: "Fresh skin fade with precise contour work",
            ro: "Skin fade proaspăt cu un contur precis",
          },
          caption: {
            en: "Skin fade contour",
            ro: "Contur skin fade",
          },
        }),
        thirdGenerationServicePortfolioItem({
          itemId: "third-generation-cut-beard-01",
          serviceId: "radu-cut-beard",
          photoId: "1503951914875-452162b0f3f1",
          alt: {
            en: "Haircut and beard styling result",
            ro: "Rezultat de tunsoare și aranjare a bărbii",
          },
          caption: {
            en: "Cut and beard styling",
            ro: "Tunsoare și aranjare barbă",
          },
        }),
        thirdGenerationServicePortfolioItem({
          itemId: "third-generation-cut-beard-02",
          serviceId: "radu-cut-beard",
          photoId: "1621605815971-fbc98d665033",
          alt: {
            en: "Finished haircut paired with a shaped beard",
            ro: "Tunsoare finalizată împreună cu o barbă conturată",
          },
          caption: {
            en: "Hair and beard finish",
            ro: "Finisaj păr și barbă",
          },
        }),
        thirdGenerationServicePortfolioItem({
          itemId: "third-generation-hot-towel-01",
          serviceId: "radu-hot-towel-beard",
          photoId: "1521590832167-7bcbfaa6381f",
          alt: {
            en: "Beard prepared for a hot towel grooming ritual",
            ro: "Barbă pregătită pentru ritualul de îngrijire cu prosop cald",
          },
          caption: {
            en: "Hot towel beard ritual",
            ro: "Ritual barbă cu prosop cald",
          },
        }),
        thirdGenerationServicePortfolioItem({
          itemId: "third-generation-hot-towel-02",
          serviceId: "radu-hot-towel-beard",
          photoId: "1560066984-138dadb4c035",
          alt: {
            en: "Finished beard grooming result after the hot towel ritual",
            ro: "Rezultat final de îngrijire a bărbii după ritualul cu prosop cald",
          },
          caption: {
            en: "Beard ritual finish",
            ro: "Finisaj ritual barbă",
          },
        }),
        thirdGenerationServicePortfolioItem({
          itemId: "third-generation-full-package-01",
          serviceId: "radu-full-package",
          photoId: "1585747860715-2ba37e788b70",
          alt: {
            en: "Complete barbering package result at 3rd Generation",
            ro: "Rezultat al pachetului complet de barbering la 3rd Generation",
          },
          caption: {
            en: "Complete barbering package",
            ro: "Pachet complet barbering",
          },
        }),
        thirdGenerationServicePortfolioItem({
          itemId: "third-generation-full-package-02",
          serviceId: "radu-full-package",
          photoId: "1599351431202-1e0f0137899a",
          alt: {
            en: "Haircut and beard finish included in the complete package",
            ro: "Finisaj de tunsoare și barbă inclus în pachetul complet",
          },
          caption: {
            en: "Complete package finish",
            ro: "Finisaj pachet complet",
          },
        }),
      ],
    },
  ]);

export interface LocationProfileReviewAuthor {
  displayName: string;
  avatarSrc: string | null;
}

export interface LocationProfileReviewReply {
  authorType: "business" | "specialist";
  authorName: string;
  body: string;
  publishedAt: string;
}

/**
 * One Location-owned review. It deliberately has no person or assignment
 * identity: workplace reviews must never be derived from Specialist rows.
 */
export interface LocationProfileReview {
  id: string;
  locationId: string;
  reviewer: LocationProfileReviewAuthor;
  rating: 1 | 2 | 3 | 4 | 5;
  publishedAt: string;
  body: string | null;
  serviceIds: readonly string[];
  verifiedBooking: boolean;
  reply: LocationProfileReviewReply | null;
}

export interface LocationProfileReviewEvidenceV1 {
  schemaVersion: 1;
  providerId: string;
  providerRevision: string;
  locationId: string;
  aggregateRating: number | null;
  aggregateReviewCount: number;
  reviews: readonly LocationProfileReview[];
}

const GLOW_ROOM_REVIEWER_NAMES = Object.freeze([
  "Elena Radu",
  "Bianca M.",
  "Diana Stancu",
  "Raluca Pavel",
  "Adriana Ionescu",
  "Teodora N.",
  "Mihaela Georgescu",
  "Alina Dumitrescu",
  "Sorina C.",
  "Nicoleta Avram",
  "Oana Petrescu",
  "Claudia Marinescu",
]);

const GLOW_ROOM_REVIEW_BODIES = Object.freeze([
  "O experiență foarte plăcută, cu multă atenție la detalii și un rezultat exact pe gustul meu.",
  "Echipa a fost punctuală, atentă și mi-a explicat fiecare etapă înainte să înceapă.",
  "Am revenit cu încredere și am găsit aceeași atmosferă relaxată și servicii foarte bine realizate.",
  "Rezultatul a rezistat impecabil, iar recomandările primite au fost clare și ușor de urmat.",
  "Spațiul este îngrijit, programarea a început la timp și întreaga experiență a fost confortabilă.",
  "Mi-a plăcut consultarea de la început și felul în care serviciul a fost adaptat preferințelor mele.",
]);

function buildGlowRoomGeneratedLocationReviews(): readonly LocationProfileReview[] {
  return Object.freeze(
    Array.from({ length: 60 }, (_, offset) => {
      const ordinal = offset + 5;
      const rating: LocationProfileReview["rating"] =
        offset % 11 === 10 ? 4 : 5;
      const publishedAt = new Date(
        Date.UTC(2026, 5, 29, 16, 0) - offset * 48 * 60 * 60 * 1_000,
      ).toISOString();

      return Object.freeze<LocationProfileReview>({
        id: `location-review-glow-room-${String(ordinal).padStart(3, "0")}`,
        locationId: "venue-glow-room",
        reviewer: Object.freeze({
          displayName:
            GLOW_ROOM_REVIEWER_NAMES[offset % GLOW_ROOM_REVIEWER_NAMES.length]!,
          avatarSrc: null,
        }),
        rating,
        publishedAt,
        body: GLOW_ROOM_REVIEW_BODIES[offset % GLOW_ROOM_REVIEW_BODIES.length]!,
        serviceIds: Object.freeze([]),
        verifiedBooking: offset % 5 !== 0,
        reply: null,
      });
    }),
  );
}

/**
 * Explicit frontend Location-review evidence for the complete Glow Room
 * scenario. These rows are authored independently from Specialist fixtures.
 */
export const LOCATION_PROFILE_REVIEW_EVIDENCE: readonly LocationProfileReviewEvidenceV1[] =
  Object.freeze<LocationProfileReviewEvidenceV1[]>([
    Object.freeze({
      schemaVersion: 1,
      providerId: LOCATION_PROFILE_REVIEW_PROVIDER_ID,
      providerRevision: "glow-room-location-review-replies-2026-08-03",
      locationId: "venue-glow-room",
      aggregateRating: 4.9,
      aggregateReviewCount: 64,
      reviews: Object.freeze<LocationProfileReview[]>([
        Object.freeze({
          id: "location-review-glow-room-001",
          locationId: "venue-glow-room",
          reviewer: Object.freeze({
            displayName: "Ana Popescu",
            avatarSrc: null,
          }),
          rating: 5,
          publishedAt: "2026-07-29T15:20:00.000Z",
          body: "Atmosferă foarte plăcută, iar machiajul a rezistat impecabil până la finalul evenimentului.",
          serviceIds: Object.freeze(["andrada-evening-makeup"]),
          verifiedBooking: true,
          reply: Object.freeze({
            authorType: "business",
            authorName: "Glow Room",
            body: "Îți mulțumim pentru vizită și pentru încredere! Ne bucurăm că experiența a fost pe măsura așteptărilor și te așteptăm cu drag să revii la Glow Room.",
            publishedAt: "2026-07-30T08:10:00.000Z",
          }),
        }),
        Object.freeze({
          id: "location-review-glow-room-002",
          locationId: "venue-glow-room",
          reviewer: Object.freeze({
            displayName: "Ioana M.",
            avatarSrc: null,
          }),
          rating: 5,
          publishedAt: "2026-07-22T11:45:00.000Z",
          body: "Programarea a început la timp, iar rezultatul laminării a fost exact cum mi-am dorit.",
          serviceIds: Object.freeze(["andrada-brow-lamination"]),
          verifiedBooking: true,
          reply: Object.freeze({
            authorType: "specialist",
            authorName: "Andrada Ivan",
            body: "Îți mulțumesc pentru mesaj și mă bucur că rezultatul laminării a fost exact cel dorit. Te aștept cu drag la următoarea programare!",
            publishedAt: "2026-07-22T16:30:00.000Z",
          }),
        }),
        Object.freeze({
          id: "location-review-glow-room-003",
          locationId: "venue-glow-room",
          reviewer: Object.freeze({
            displayName: "Cristina Dobre",
            avatarSrc: null,
          }),
          rating: 5,
          publishedAt: "2026-07-14T09:05:00.000Z",
          body: "Spațiul este curat și liniștit, iar echipa explică fiecare etapă cu multă atenție.",
          serviceIds: Object.freeze(["andrada-lash-lift"]),
          verifiedBooking: true,
          reply: null,
        }),
        Object.freeze({
          id: "location-review-glow-room-004",
          locationId: "venue-glow-room",
          reviewer: Object.freeze({
            displayName: "Mara T.",
            avatarSrc: null,
          }),
          rating: 4,
          publishedAt: "2026-07-03T17:30:00.000Z",
          body: null,
          serviceIds: Object.freeze([]),
          verifiedBooking: true,
          reply: null,
        }),
        ...buildGlowRoomGeneratedLocationReviews(),
      ]),
    }),
  ]);

export type LocationProfileCommerceEntry =
  | {
      kind: "memberships";
      locationId: string;
      href: string;
      providerId: string;
      catalogVersion: string;
      catalogItemIds: readonly string[];
    }
  | {
      kind: "packages";
      locationId: string;
      href: string;
      providerId: string;
      catalogVersion: string;
      catalogOwnerUserLocationId: string;
      catalogItemIds: readonly string[];
    }
  | {
      kind: "gift-cards";
      locationId: string;
      href: string;
      catalogVersion: string;
      giftCardProgramVersion: string;
      checkoutProviderId: string;
      paymentProviderId: string;
      purchaseProviderId: string;
      catalogItemIds: readonly string[];
      customValueEnabled: boolean;
    };

export type LocationProfileNearbyLocation = Readonly<MarketplaceVenue>;

export interface LocationProfileNearbyLocationEvidenceV1 {
  schemaVersion: 1;
  providerId: string;
  providerRevision: string;
  locationId: string;
  nearbyLocationIds: readonly string[];
}

/** Explicit merchandising order; proximity is never inferred client-side. */
export const LOCATION_PROFILE_NEARBY_EVIDENCE: readonly LocationProfileNearbyLocationEvidenceV1[] =
  Object.freeze<LocationProfileNearbyLocationEvidenceV1[]>([
    Object.freeze({
      schemaVersion: 1,
      providerId: LOCATION_PROFILE_NEARBY_PROVIDER_ID,
      providerRevision: "glow-room-nearby-locations-2026-08-03",
      locationId: "venue-glow-room",
      nearbyLocationIds: Object.freeze([
        "venue-moonlight-nails",
        "venue-hair-hood",
        "venue-laserty",
        "venue-blind-barbers",
        "venue-3rd-generation",
        "venue-skin-theory",
        "venue-helen-beauty",
        "venue-pure-spa",
      ]),
    }),
  ]);

export interface LocationProfileTeamMember {
  assignment: SpecialistLocationTeamAssignment;
  person: SpecialistPerson;
  profileHref: string | null;
}

export interface LocationProfileServiceEntry {
  assignment: SpecialistAssignment;
  person: SpecialistPerson;
  service: SpecialistService;
  offering: SpecialistServiceOffering;
}

export interface LocationBookableCatalogV1 {
  schemaVersion: 1;
  providerId: typeof LOCATION_PROFILE_PROVIDER_ID;
  providerRevision: typeof LOCATION_PROFILE_PROVIDER_REVISION;
  locationId: string;
  catalogVersion: string;
  workplace: SpecialistWorkplace;
  catalogOwnerAssignment: SpecialistAssignment;
  team: readonly LocationProfileTeamMember[];
  serviceEntries: readonly LocationProfileServiceEntry[];
  packages: readonly SpecialistPackage[];
  servicesHref: string;
  packagesHref: string | null;
}

export interface LocationProfileSnapshotV2 {
  schemaVersion: typeof LOCATION_PROFILE_SNAPSHOT_SCHEMA_VERSION;
  providerId: typeof LOCATION_PROFILE_PROVIDER_ID;
  providerRevision: typeof LOCATION_PROFILE_SNAPSHOT_PROVIDER_REVISION;
  state: "published";
  locationId: string;
  canonicalHref: string;
  name: string;
  address: string;
  category: string;
  imageSrc: string | null;
  imageAlt: string;
  images: readonly MarketplaceVenueImage[];
  rating: number | null;
  reviewCount: number;
  badge: ResultsBadge | null;
  coordinates: ResultsCoordinates;
  minimumPrice: ResultsPrice | null;
  acceptsGroups: boolean;
  audience: readonly ResultsAudience[];
  amenities: readonly ResultsAmenity[];
  businessEvidenceProvider: LocationProfileProviderLineage | null;
  timeZone: string | null;
  about: LocationProfileLocalizedText | null;
  hours: readonly LocationProfileWeeklyHoursEntry[] | null;
  additionalInformation: readonly LocationProfileAdditionalInformationEntry[];
  contact: LocationProfileContactEvidence | null;
  directionsHref: string | null;
  reviewEvidenceProvider: LocationProfileProviderLineage | null;
  reviews: readonly LocationProfileReview[];
  team: readonly LocationProfileTeamMember[];
  portfolioEvidenceProvider: LocationProfileProviderLineage | null;
  portfolioItems: readonly LocationProfilePortfolioItemV2[];
  bookableCatalog: LocationBookableCatalogV1 | null;
  commerceEntries: readonly LocationProfileCommerceEntry[];
  nearbyLocationsEvidenceProvider: LocationProfileProviderLineage | null;
  nearbyLocations: readonly LocationProfileNearbyLocation[];
}

function isLocationProfileIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 160
  );
}

function isLocationProfileCopy(
  value: unknown,
  maximumLength = 5_000,
): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= maximumLength
  );
}

function isCanonicalIsoInstant(value: unknown): value is string {
  if (typeof value !== "string") return false;

  const instant = new Date(value);
  return Number.isFinite(instant.getTime()) && instant.toISOString() === value;
}

function isLocationProfileRecord(
  value: unknown,
): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isDenseLocationProfileArray(value: unknown): value is unknown[] {
  if (!Array.isArray(value)) return false;

  try {
    for (let index = 0; index < value.length; index += 1) {
      if (!Object.prototype.hasOwnProperty.call(value, index)) return false;
    }

    return true;
  } catch {
    return false;
  }
}

function hasExactLocationProfileKeys(
  value: unknown,
  expectedKeys: readonly string[],
): value is Record<string, unknown> {
  if (!isLocationProfileRecord(value)) return false;

  const actualKeys = Object.keys(value).sort();
  const sortedExpectedKeys = [...expectedKeys].sort();

  return (
    actualKeys.length === sortedExpectedKeys.length &&
    actualKeys.every((key, index) => key === sortedExpectedKeys[index])
  );
}

function cloneDeepFrozen<T>(value: T): T {
  if (Array.isArray(value)) {
    return Object.freeze(value.map((entry) => cloneDeepFrozen(entry))) as T;
  }

  if (value !== null && typeof value === "object") {
    return Object.freeze(
      Object.fromEntries(
        Object.entries(value).map(([key, entry]) => [
          key,
          cloneDeepFrozen(entry),
        ]),
      ),
    ) as T;
  }

  return value;
}

function isCanonicalIanaTimeZone(value: unknown): value is string {
  if (!isLocationProfileCopy(value, 100)) return false;

  try {
    return (
      new Intl.DateTimeFormat("en-US", { timeZone: value }).resolvedOptions()
        .timeZone === value
    );
  } catch {
    return false;
  }
}

function isValidLocationProfileLocalizedText(
  value: unknown,
  maximumLength = 5_000,
): value is LocationProfileLocalizedText {
  return (
    hasExactLocationProfileKeys(value, ["en", "ro"]) &&
    isLocationProfileCopy(value.en, maximumLength) &&
    isLocationProfileCopy(value.ro, maximumLength)
  );
}

function parseLocationProfileClock(value: unknown) {
  if (typeof value !== "string") return null;

  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  return hours * 60 + minutes;
}

function isValidLocationProfileIntervals(value: unknown): boolean {
  if (!Array.isArray(value) || value.length > 12) return false;

  let previousClose = -1;

  return value.every((interval) => {
    if (!hasExactLocationProfileKeys(interval, ["opensAt", "closesAt"])) {
      return false;
    }

    const opensAt = parseLocationProfileClock(interval.opensAt);
    const closesAt = parseLocationProfileClock(interval.closesAt);
    if (
      opensAt === null ||
      closesAt === null ||
      closesAt <= opensAt ||
      opensAt < previousClose
    ) {
      return false;
    }

    previousClose = closesAt;
    return true;
  });
}

function isValidLocationProfileWeeklyHours(
  value: unknown,
): value is readonly LocationProfileWeeklyHoursEntry[] {
  return (
    Array.isArray(value) &&
    value.length === LOCATION_PROFILE_WEEKDAYS.length &&
    value.every(
      (entry, index) =>
        hasExactLocationProfileKeys(entry, ["day", "intervals"]) &&
        entry.day === LOCATION_PROFILE_WEEKDAYS[index] &&
        isValidLocationProfileIntervals(entry.intervals),
    )
  );
}

function isValidLocationProfileContact(
  value: unknown,
): value is LocationProfileContactEvidence {
  if (!hasExactLocationProfileKeys(value, ["phone", "email", "websiteHref"])) {
    return false;
  }

  const validPhone =
    value.phone === null ||
    (typeof value.phone === "string" && /^\+[1-9]\d{7,14}$/.test(value.phone));
  const validEmail =
    value.email === null ||
    (typeof value.email === "string" &&
      value.email === value.email.trim() &&
      value.email.length <= 254 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email));

  let validWebsite = value.websiteHref === null;
  if (typeof value.websiteHref === "string") {
    try {
      const url = new URL(value.websiteHref);
      validWebsite =
        value.websiteHref.length <= 2_000 &&
        url.protocol === "https:" &&
        url.username === "" &&
        url.password === "" &&
        url.hash === "" &&
        url.href === value.websiteHref;
    } catch {
      validWebsite = false;
    }
  }

  return (
    validPhone &&
    validEmail &&
    validWebsite &&
    (value.phone !== null || value.email !== null || value.websiteHref !== null)
  );
}

function isValidLocationProfileDirectionsHref(
  value: unknown,
): value is string | null {
  if (value === null) return true;
  if (typeof value !== "string" || value.length > 2_000) return false;

  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.username === "" &&
      url.password === "" &&
      url.hash === "" &&
      url.href === value
    );
  } catch {
    return false;
  }
}

function isCanonicalLocationProfileHttpsUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 2_000) return false;

  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.username === "" &&
      url.password === "" &&
      url.hash === "" &&
      url.href === value
    );
  } catch {
    return false;
  }
}

function isValidLocationProfileAdditionalInformation(value: unknown) {
  if (!Array.isArray(value) || value.length > 50) return false;

  const identifiers = value.flatMap((entry) =>
    isLocationProfileRecord(entry) && typeof entry.informationId === "string"
      ? [entry.informationId]
      : [],
  );

  return (
    identifiers.length === value.length &&
    new Set(identifiers).size === identifiers.length &&
    value.every(
      (entry) =>
        hasExactLocationProfileKeys(entry, [
          "informationId",
          "title",
          "description",
        ]) &&
        isLocationProfileIdentifier(entry.informationId) &&
        isValidLocationProfileLocalizedText(entry.title) &&
        isValidLocationProfileLocalizedText(entry.description),
    )
  );
}

function isValidLocationProfileBusinessEvidence(
  value: unknown,
  venue: VenueResult,
): value is LocationProfileBusinessEvidenceV1 {
  return (
    hasExactLocationProfileKeys(value, [
      "schemaVersion",
      "providerId",
      "providerRevision",
      "locationId",
      "timeZone",
      "about",
      "weeklyHours",
      "additionalInformation",
      "contact",
      "directionsHref",
    ]) &&
    value.schemaVersion === LOCATION_PROFILE_SCHEMA_VERSION &&
    value.providerId === LOCATION_PROFILE_BUSINESS_PROVIDER_ID &&
    isLocationProfileIdentifier(value.providerRevision) &&
    value.locationId === venue.id &&
    isCanonicalIanaTimeZone(value.timeZone) &&
    isValidLocationProfileLocalizedText(
      value.about,
      LOCATION_PROFILE_ABOUT_MAX_LENGTH,
    ) &&
    isValidLocationProfileWeeklyHours(value.weeklyHours) &&
    isValidLocationProfileAdditionalInformation(value.additionalInformation) &&
    (value.contact === null || isValidLocationProfileContact(value.contact)) &&
    isValidLocationProfileDirectionsHref(value.directionsHref)
  );
}

export interface ResolvedLocationProfileBusinessEvidence {
  provider: LocationProfileProviderLineage;
  timeZone: string;
  about: LocationProfileLocalizedText;
  hours: readonly LocationProfileWeeklyHoursEntry[];
  additionalInformation: readonly LocationProfileAdditionalInformationEntry[];
  contact: LocationProfileContactEvidence | null;
  directionsHref: string | null;
}

export function resolveLocationProfileBusinessEvidence(
  venue: VenueResult,
  evidenceSource: readonly LocationProfileBusinessEvidenceV1[] = LOCATION_PROFILE_BUSINESS_EVIDENCE,
): ResolvedLocationProfileBusinessEvidence | null {
  const candidates = evidenceSource.filter(
    (evidence) =>
      isLocationProfileRecord(evidence) && evidence.locationId === venue.id,
  );

  if (candidates.length !== 1) return null;

  const evidence = candidates[0];
  if (!isValidLocationProfileBusinessEvidence(evidence, venue)) return null;

  return Object.freeze({
    provider: cloneDeepFrozen({
      providerId: evidence.providerId,
      providerRevision: evidence.providerRevision,
    }),
    timeZone: evidence.timeZone,
    about: cloneDeepFrozen(evidence.about),
    hours: cloneDeepFrozen(evidence.weeklyHours),
    additionalInformation: cloneDeepFrozen(evidence.additionalInformation),
    contact: evidence.contact ? cloneDeepFrozen(evidence.contact) : null,
    directionsHref: evidence.directionsHref,
  });
}

function resolveLocationProfilePortfolioItem(
  value: unknown,
  venue: VenueResult,
  team: readonly LocationProfileTeamMember[],
  bookableCatalog: LocationBookableCatalogV1 | null,
): LocationProfilePortfolioItemV2 | null {
  if (!isLocationProfileRecord(value)) return null;

  if (
    !isLocationProfileIdentifier(value.itemId) ||
    value.locationId !== venue.id ||
    !isValidLocationProfileLocalizedText(value.alt, 300) ||
    (value.caption !== null &&
      !isValidLocationProfileLocalizedText(value.caption, 1_000))
  ) {
    return null;
  }

  const itemId = value.itemId;
  const alt = value.alt;
  const caption = value.caption;

  if (value.sourceKind === "service") {
    if (
      !hasExactLocationProfileKeys(value, [
        "sourceKind",
        "itemId",
        "locationId",
        "serviceTag",
        "src",
        "alt",
        "caption",
      ]) ||
      !isCanonicalLocationProfileHttpsUrl(value.src)
    ) {
      return null;
    }

    let service: LocationProfilePortfolioServiceReference | null = null;
    if (value.serviceTag !== null) {
      if (
        !hasExactLocationProfileKeys(value.serviceTag, [
          "catalogVersion",
          "serviceId",
        ]) ||
        !isLocationProfileIdentifier(value.serviceTag.catalogVersion) ||
        !isLocationProfileIdentifier(value.serviceTag.serviceId) ||
        !bookableCatalog ||
        bookableCatalog.locationId !== venue.id ||
        bookableCatalog.catalogVersion !== value.serviceTag.catalogVersion
      ) {
        return null;
      }

      const serviceId = value.serviceTag.serviceId;
      const matches = bookableCatalog.serviceEntries.filter(
        ({ service: candidate }) =>
          candidate.serviceId === serviceId,
      );
      if (matches.length !== 1) return null;

      const matchedService = matches[0]!.service;
      service = {
        catalogVersion: bookableCatalog.catalogVersion,
        serviceId: matchedService.serviceId,
        serviceName: matchedService.name,
        categoryId: matchedService.categoryId,
        bookingHref: buildLocationServicesHref(venue.id, {
          configureServiceId: matchedService.serviceId,
        }),
      };
    }

    return cloneDeepFrozen({
      itemId,
      locationId: venue.id,
      source: { kind: "service", service },
      src: value.src,
      alt,
      caption,
    });
  }

  if (
    value.sourceKind !== "team" ||
    !hasExactLocationProfileKeys(value, [
      "sourceKind",
      "itemId",
      "locationId",
      "contributor",
      "professionalPortfolioItemId",
      "alt",
      "caption",
    ]) ||
    !hasExactLocationProfileKeys(value.contributor, ["personId", "displayName"])
  ) {
    return null;
  }

  const contributor = value.contributor;
  const contributorBelongsToPublishedTeam = team.some(
    (member) =>
      member.person.personId === contributor.personId &&
      member.person.name === contributor.displayName,
  );
  if (
    !isLocationProfileIdentifier(contributor.personId) ||
    !isLocationProfileCopy(contributor.displayName, 500) ||
    !contributorBelongsToPublishedTeam ||
    !isLocationProfileIdentifier(value.professionalPortfolioItemId)
  ) {
    return null;
  }

  const resolvedContributor: LocationProfilePortfolioContributor = {
    personId: contributor.personId,
    displayName: contributor.displayName,
  };
  const professionalPortfolioItemId = value.professionalPortfolioItemId;

  const professionalMatches = getSpecialistProfilePortfolioItems(
    resolvedContributor.personId,
  ).filter(
    (item) => item.id === professionalPortfolioItemId,
  );
  if (
    professionalMatches.length !== 1 ||
    !isCanonicalLocationProfileHttpsUrl(professionalMatches[0]!.src)
  ) {
    return null;
  }

  return cloneDeepFrozen({
    itemId,
    locationId: venue.id,
    source: {
      kind: "team",
      contributor: resolvedContributor,
      professionalPortfolioItemId,
    },
    src: professionalMatches[0]!.src,
    alt,
    caption,
  });
}

function resolveLocationProfilePortfolioEvidenceValue(
  value: unknown,
  venue: VenueResult,
  team: readonly LocationProfileTeamMember[],
  bookableCatalog: LocationBookableCatalogV1 | null,
): ResolvedLocationProfilePortfolio | null {
  if (
    !hasExactLocationProfileKeys(value, [
      "schemaVersion",
      "providerId",
      "providerRevision",
      "locationId",
      "items",
    ]) ||
    value.schemaVersion !== LOCATION_PROFILE_PORTFOLIO_SCHEMA_VERSION ||
    value.providerId !== LOCATION_PROFILE_PORTFOLIO_PROVIDER_ID ||
    !isLocationProfileIdentifier(value.providerRevision) ||
    value.locationId !== venue.id ||
    !isDenseLocationProfileArray(value.items) ||
    value.items.length === 0 ||
    value.items.length > LOCATION_PROFILE_PORTFOLIO_MAX_ITEMS
  ) {
    return null;
  }

  const resolvedItems = value.items.map((item) =>
    resolveLocationProfilePortfolioItem(item, venue, team, bookableCatalog),
  );
  if (resolvedItems.some((item) => item === null)) return null;

  const items = resolvedItems as LocationProfilePortfolioItemV2[];
  const itemIds = items.map((item) => item.itemId);
  const sources = items.map((item) => item.src);
  if (
    new Set(itemIds).size !== itemIds.length ||
    new Set(sources).size !== sources.length
  ) {
    return null;
  }

  return Object.freeze({
    provider: cloneDeepFrozen({
      providerId: value.providerId,
      providerRevision: value.providerRevision,
    }),
    items: cloneDeepFrozen(items),
  });
}

export interface ResolvedLocationProfilePortfolio {
  provider: LocationProfileProviderLineage;
  items: readonly LocationProfilePortfolioItemV2[];
}

export function resolveLocationProfilePortfolioEvidence(
  venue: VenueResult,
  team: readonly LocationProfileTeamMember[],
  bookableCatalog: LocationBookableCatalogV1 | null,
  evidenceSource: unknown = LOCATION_PROFILE_PORTFOLIO_EVIDENCE,
): ResolvedLocationProfilePortfolio | null {
  if (!isDenseLocationProfileArray(evidenceSource)) return null;

  const candidates = evidenceSource.filter(
    (evidence) =>
      isLocationProfileRecord(evidence) && evidence.locationId === venue.id,
  );

  if (candidates.length !== 1) return null;
  return resolveLocationProfilePortfolioEvidenceValue(
    candidates[0],
    venue,
    team,
    bookableCatalog,
  );
}

function isValidLocationProfileReview(
  value: unknown,
  locationId: string,
): value is LocationProfileReview {
  if (
    !hasExactLocationProfileKeys(value, [
      "id",
      "locationId",
      "reviewer",
      "rating",
      "publishedAt",
      "body",
      "serviceIds",
      "verifiedBooking",
      "reply",
    ])
  ) {
    return false;
  }

  const review = value;
  const serviceIds = review.serviceIds;
  const reply = review.reply;
  const reviewer = review.reviewer;

  if (
    !hasExactLocationProfileKeys(reviewer, ["displayName", "avatarSrc"]) ||
    !Array.isArray(serviceIds) ||
    (reply !== null &&
      !hasExactLocationProfileKeys(reply, [
        "authorType",
        "authorName",
        "body",
        "publishedAt",
      ]))
  ) {
    return false;
  }

  return (
    isLocationProfileIdentifier(review.id) &&
    review.locationId === locationId &&
    isLocationProfileCopy(reviewer.displayName, 500) &&
    (reviewer.avatarSrc === null ||
      isLocationProfileCopy(reviewer.avatarSrc, 2_000)) &&
    typeof review.rating === "number" &&
    Number.isSafeInteger(review.rating) &&
    review.rating >= 1 &&
    review.rating <= 5 &&
    isCanonicalIsoInstant(review.publishedAt) &&
    (review.body === null || isLocationProfileCopy(review.body)) &&
    serviceIds.length <= 100 &&
    serviceIds.every(isLocationProfileIdentifier) &&
    new Set(serviceIds).size === serviceIds.length &&
    typeof review.verifiedBooking === "boolean" &&
    (reply === null ||
      ((reply.authorType === "business" ||
        reply.authorType === "specialist") &&
        isLocationProfileCopy(reply.authorName, 500) &&
        isLocationProfileCopy(reply.body) &&
        isCanonicalIsoInstant(reply.publishedAt) &&
        Date.parse(reply.publishedAt) >= Date.parse(review.publishedAt)))
  );
}

function isValidLocationProfileReviewEvidence(
  value: unknown,
  venue: VenueResult,
): value is LocationProfileReviewEvidenceV1 {
  if (
    !hasExactLocationProfileKeys(value, [
      "schemaVersion",
      "providerId",
      "providerRevision",
      "locationId",
      "aggregateRating",
      "aggregateReviewCount",
      "reviews",
    ]) ||
    value.schemaVersion !== LOCATION_PROFILE_SCHEMA_VERSION ||
    value.providerId !== LOCATION_PROFILE_REVIEW_PROVIDER_ID ||
    !isLocationProfileIdentifier(value.providerRevision) ||
    value.locationId !== venue.id ||
    (value.aggregateRating !== null &&
      (typeof value.aggregateRating !== "number" ||
        !Number.isFinite(value.aggregateRating))) ||
    value.aggregateRating !== venue.rating ||
    typeof value.aggregateReviewCount !== "number" ||
    !Number.isSafeInteger(value.aggregateReviewCount) ||
    value.aggregateReviewCount < 0 ||
    value.aggregateReviewCount !== venue.reviewCount ||
    !Array.isArray(value.reviews) ||
    value.reviews.length === 0 ||
    value.reviews.length > value.aggregateReviewCount ||
    !value.reviews.every((review) =>
      isValidLocationProfileReview(review, venue.id),
    )
  ) {
    return false;
  }

  const reviewIds = value.reviews.map((review) => review.id);
  return new Set(reviewIds).size === reviewIds.length;
}

function cloneLocationProfileReview(
  review: LocationProfileReview,
): LocationProfileReview {
  return Object.freeze({
    ...review,
    reviewer: Object.freeze({ ...review.reviewer }),
    serviceIds: Object.freeze([...review.serviceIds]),
    reply: review.reply ? Object.freeze({ ...review.reply }) : null,
  });
}

export interface ResolvedLocationProfileReviews {
  provider: LocationProfileProviderLineage;
  reviews: readonly LocationProfileReview[];
}

export function resolveLocationProfileReviewEvidence(
  venue: VenueResult,
  evidenceSource: unknown = LOCATION_PROFILE_REVIEW_EVIDENCE,
): ResolvedLocationProfileReviews | null {
  if (!Array.isArray(evidenceSource)) return null;

  const candidates = evidenceSource.filter(
    (evidence) =>
      isLocationProfileRecord(evidence) && evidence.locationId === venue.id,
  );

  if (candidates.length !== 1) return null;

  const evidence = candidates[0];
  if (!isValidLocationProfileReviewEvidence(evidence, venue)) return null;

  return Object.freeze({
    provider: Object.freeze({
      providerId: evidence.providerId,
      providerRevision: evidence.providerRevision,
    }),
    reviews: Object.freeze(evidence.reviews.map(cloneLocationProfileReview)),
  });
}

function cloneNearbyLocation(
  venue: VenueResult,
): LocationProfileNearbyLocation {
  return Object.freeze({
    id: venue.id,
    name: venue.name,
    href: venue.href,
    imageSrc: venue.imageSrc,
    imageAlt: venue.imageAlt,
    images: Object.freeze(
      venue.images.map((image) => Object.freeze({ ...image })),
    ),
    rating: venue.rating,
    reviewCount: venue.reviewCount,
    address: venue.address,
    category: venue.category,
    isFavorite: venue.isFavorite,
    badge: venue.badge ? Object.freeze({ ...venue.badge }) : undefined,
  });
}

export interface ResolvedLocationProfileNearbyLocations {
  provider: LocationProfileProviderLineage;
  locations: readonly LocationProfileNearbyLocation[];
}

function isValidLocationProfileNearbyImage(
  value: unknown,
): value is MarketplaceVenueImage {
  if (!isLocationProfileRecord(value)) return false;

  const expectedKeys = ["id", "src", "alt"];
  if ("width" in value) expectedKeys.push("width");
  if ("height" in value) expectedKeys.push("height");
  if (!hasExactLocationProfileKeys(value, expectedKeys)) return false;

  return (
    isLocationProfileIdentifier(value.id) &&
    isLocationProfileCopy(value.src, 2_000) &&
    typeof value.alt === "string" &&
    value.alt === value.alt.trim() &&
    value.alt.length <= 500 &&
    (value.width === undefined ||
      (typeof value.width === "number" &&
        Number.isSafeInteger(value.width) &&
        value.width > 0)) &&
    (value.height === undefined ||
      (typeof value.height === "number" &&
        Number.isSafeInteger(value.height) &&
        value.height > 0))
  );
}

function isValidLocationProfileNearbyVenue(
  value: unknown,
  locationId: string,
): value is VenueResult {
  if (
    !isLocationProfileRecord(value) ||
    value.kind !== "venue" ||
    value.id !== locationId ||
    value.href !== buildLocationProfilePath(locationId) ||
    !isLocationProfileCopy(value.name, 500) ||
    (value.imageSrc !== null &&
      !isLocationProfileCopy(value.imageSrc, 2_000)) ||
    typeof value.imageAlt !== "string" ||
    value.imageAlt !== value.imageAlt.trim() ||
    value.imageAlt.length > 500 ||
    !Array.isArray(value.images) ||
    value.images.length === 0 ||
    value.images.length > 100 ||
    !value.images.every(isValidLocationProfileNearbyImage) ||
    (value.rating !== null &&
      (typeof value.rating !== "number" ||
        !Number.isFinite(value.rating) ||
        value.rating < 1 ||
        value.rating > 5)) ||
    typeof value.reviewCount !== "number" ||
    !Number.isSafeInteger(value.reviewCount) ||
    value.reviewCount < 0 ||
    typeof value.isFavorite !== "boolean" ||
    !isLocationProfileCopy(value.address, 1_000) ||
    !isLocationProfileCopy(value.category, 500)
  ) {
    return false;
  }

  if (value.badge !== undefined) {
    if (
      !hasExactLocationProfileKeys(value.badge, [
        "label",
        ...(isLocationProfileRecord(value.badge) && "variant" in value.badge
          ? ["variant"]
          : []),
      ]) ||
      !isLocationProfileCopy(value.badge.label, 200) ||
      (value.badge.variant !== undefined &&
        value.badge.variant !== "default" &&
        value.badge.variant !== "featured" &&
        value.badge.variant !== "recommended" &&
        value.badge.variant !== "new" &&
        value.badge.variant !== "popular")
    ) {
      return false;
    }
  }

  const imageIds = value.images.map((image) => image.id);
  const imageSources = value.images.map((image) => image.src);
  return (
    new Set(imageIds).size === imageIds.length &&
    new Set(imageSources).size === imageSources.length
  );
}

function isValidLocationProfileNearbyEvidence(
  value: unknown,
  venue: VenueResult,
): value is LocationProfileNearbyLocationEvidenceV1 {
  if (
    !hasExactLocationProfileKeys(value, [
      "schemaVersion",
      "providerId",
      "providerRevision",
      "locationId",
      "nearbyLocationIds",
    ]) ||
    value.schemaVersion !== LOCATION_PROFILE_SCHEMA_VERSION ||
    value.providerId !== LOCATION_PROFILE_NEARBY_PROVIDER_ID ||
    !isLocationProfileIdentifier(value.providerRevision) ||
    value.locationId !== venue.id ||
    !Array.isArray(value.nearbyLocationIds) ||
    value.nearbyLocationIds.length === 0 ||
    value.nearbyLocationIds.length > 12 ||
    value.nearbyLocationIds.includes(venue.id) ||
    !value.nearbyLocationIds.every(isLocationProfileIdentifier) ||
    new Set(value.nearbyLocationIds).size !== value.nearbyLocationIds.length
  ) {
    return false;
  }

  return true;
}

export function resolveLocationProfileNearbyLocationEvidence(
  venue: VenueResult,
  evidenceSource: unknown = LOCATION_PROFILE_NEARBY_EVIDENCE,
  marketplaceSource: unknown = VENUE_RESULTS_FIXTURES,
): ResolvedLocationProfileNearbyLocations | null {
  if (!Array.isArray(evidenceSource) || !Array.isArray(marketplaceSource)) {
    return null;
  }

  const candidates = evidenceSource.filter(
    (evidence) =>
      isLocationProfileRecord(evidence) && evidence.locationId === venue.id,
  );

  if (candidates.length !== 1) return null;

  const evidence = candidates[0];
  if (!isValidLocationProfileNearbyEvidence(evidence, venue)) return null;

  const nearbyLocations: VenueResult[] = [];
  for (const locationId of evidence.nearbyLocationIds) {
    const locationCandidates = marketplaceSource.filter(
      (candidate) =>
        isLocationProfileRecord(candidate) && candidate.id === locationId,
    );
    if (
      locationCandidates.length !== 1 ||
      !isValidLocationProfileNearbyVenue(locationCandidates[0], locationId)
    ) {
      return null;
    }
    nearbyLocations.push(locationCandidates[0]);
  }

  return Object.freeze({
    provider: Object.freeze({
      providerId: evidence.providerId,
      providerRevision: evidence.providerRevision,
    }),
    locations: Object.freeze(
      nearbyLocations.map((location) => cloneNearbyLocation(location)),
    ),
  });
}

function workplaceMatchesVenue(
  workplace: SpecialistWorkplace,
  venue: VenueResult,
) {
  return (
    workplace.locationId === venue.id &&
    workplace.name === venue.name &&
    workplace.address === venue.address &&
    workplace.coordinates.latitude === venue.coordinates.latitude &&
    workplace.coordinates.longitude === venue.coordinates.longitude
  );
}

function resolveLocationTeam(
  venue: VenueResult,
  workplace: SpecialistWorkplace | undefined,
) {
  if (!workplace || !workplaceMatchesVenue(workplace, venue)) {
    return [];
  }

  return SPECIALIST_LOCATION_TEAM_ASSIGNMENTS.filter(
    (assignment) =>
      assignment.locationId === venue.id && assignment.isPubliclyVisible,
  )
    .flatMap((assignment): LocationProfileTeamMember[] => {
      const person = getSpecialistPerson(assignment.personId);
      if (!person) return [];
      const profileAssignment = SPECIALIST_ASSIGNMENTS.find(
        (candidate) =>
          candidate.userLocationId === assignment.userLocationId &&
          candidate.personId === assignment.personId &&
          candidate.locationId === assignment.locationId &&
          candidate.isPublicProfileVisible,
      );

      return [
        {
          assignment,
          person,
          profileHref: profileAssignment
            ? buildSpecialistProfilePath({
                locationId: assignment.locationId,
                userLocationId: assignment.userLocationId,
              })
            : null,
        },
      ];
    })
    .sort((left, right) => left.assignment.order - right.assignment.order);
}

function resolveLocationBookableCatalog(
  venue: VenueResult,
  team: readonly LocationProfileTeamMember[],
): LocationBookableCatalogV1 | null {
  const catalog = resolveLocationAppointmentCatalog(venue.id);

  if (!catalog || !workplaceMatchesVenue(catalog.workplace, venue)) {
    return null;
  }

  // The shared adapter is the sole publication/completeness gate. This layer
  // only reshapes its already validated service/offering pairs for the page.
  const catalogOwnerPerson = getSpecialistPerson(
    catalog.catalogAssignment.personId,
  );
  if (!catalogOwnerPerson) return null;

  const serviceEntries = catalog.offerings.map((offering, index) => ({
    assignment: catalog.catalogAssignment,
    person: catalogOwnerPerson,
    service: catalog.services[index]!,
    offering,
  }));
  const packages = catalog.workplace.offersPackages
    ? getSpecialistPackagesForAssignment(
        catalog.catalogAssignment.userLocationId,
        venue.id,
      )
    : [];

  return cloneDeepFrozen({
    schemaVersion: LOCATION_PROFILE_SCHEMA_VERSION,
    providerId: LOCATION_PROFILE_PROVIDER_ID,
    providerRevision: LOCATION_PROFILE_PROVIDER_REVISION,
    locationId: venue.id,
    catalogVersion: catalog.workplace.catalogVersion,
    workplace: catalog.workplace,
    catalogOwnerAssignment: catalog.catalogAssignment,
    team,
    serviceEntries,
    packages,
    servicesHref: buildLocationServicesHref(venue.id),
    packagesHref:
      packages.length > 0
        ? buildLocationServicesHref(venue.id, { catalog: "packages" })
        : null,
  });
}

function resolveMembershipCommerceEntry(
  venue: VenueResult,
  workplace: SpecialistWorkplace | undefined,
): Extract<LocationProfileCommerceEntry, { kind: "memberships" }> | null {
  if (!workplace || !workplaceMatchesVenue(workplace, venue)) return null;

  const memberships = getMembershipsForLocation(venue.id).flatMap(
    (membership) => {
      const resolution = resolveMembershipOnlinePurchase(
        venue.id,
        membership.membershipId,
      );
      return resolution.ok ? [resolution.membership] : [];
    },
  );
  const firstMembership = memberships[0];
  const href = buildLocationMembershipsPath(venue.id);

  if (
    !firstMembership ||
    !href ||
    memberships.some(
      (membership) =>
        membership.locationId !== venue.id ||
        membership.providerId !== firstMembership.providerId ||
        membership.catalogVersion !== firstMembership.catalogVersion,
    ) ||
    new Set(memberships.map((membership) => membership.membershipId)).size !==
      memberships.length
  ) {
    return null;
  }

  return Object.freeze({
    kind: "memberships",
    locationId: venue.id,
    href,
    providerId: firstMembership.providerId,
    catalogVersion: firstMembership.catalogVersion,
    catalogItemIds: Object.freeze(
      memberships.map((membership) => membership.membershipId),
    ),
  });
}

function resolvePackageCommerceEntry(
  venue: VenueResult,
  bookableCatalog: LocationBookableCatalogV1 | null,
): Extract<LocationProfileCommerceEntry, { kind: "packages" }> | null {
  if (!bookableCatalog?.workplace.offersPackages) return null;

  const packages = getSpecialistPackagesForAssignment(
    bookableCatalog.catalogOwnerAssignment.userLocationId,
    venue.id,
  );
  const validations = packages.map(validateSpecialistPackageOnlinePurchase);

  if (
    validations.length === 0 ||
    validations.some((validation) => !validation.valid)
  ) {
    return null;
  }

  const publishedPackages = validations.flatMap((validation) =>
    validation.valid ? [validation.packageItem] : [],
  );
  const firstPackage = publishedPackages[0];

  if (
    !firstPackage?.onlinePublication.providerId ||
    firstPackage.catalogVersion !== bookableCatalog.catalogVersion ||
    publishedPackages.some(
      (packageItem) =>
        packageItem.locationId !== venue.id ||
        packageItem.userLocationId !==
          bookableCatalog.catalogOwnerAssignment.userLocationId ||
        packageItem.catalogVersion !== firstPackage.catalogVersion ||
        packageItem.onlinePublication.providerId !==
          firstPackage.onlinePublication.providerId,
    ) ||
    new Set(publishedPackages.map((packageItem) => packageItem.packageId))
      .size !== publishedPackages.length
  ) {
    return null;
  }

  return Object.freeze({
    kind: "packages",
    locationId: venue.id,
    href: buildLocationServicesHref(venue.id, { catalog: "packages" }),
    providerId: firstPackage.onlinePublication.providerId,
    catalogVersion: firstPackage.catalogVersion,
    catalogOwnerUserLocationId:
      bookableCatalog.catalogOwnerAssignment.userLocationId,
    catalogItemIds: Object.freeze(
      publishedPackages.map((packageItem) => packageItem.packageId),
    ),
  });
}

function resolveGiftCardCommerceEntry(
  venue: VenueResult,
  workplace: SpecialistWorkplace | undefined,
): Extract<LocationProfileCommerceEntry, { kind: "gift-cards" }> | null {
  if (!workplace || !workplaceMatchesVenue(workplace, venue)) return null;

  const catalog = getBusinessGiftCardCatalog(venue.id);
  const href = buildLocationGiftCardsPath(venue.id);

  if (
    !catalog ||
    !href ||
    catalog.locationId !== venue.id ||
    !catalog.eligibleLocationIds.includes(venue.id) ||
    catalog.businessSnapshot.name !== venue.name ||
    catalog.businessSnapshot.address !== venue.address ||
    !catalog.publication.checkoutProviderId ||
    !catalog.publication.paymentProviderId ||
    !catalog.publication.purchaseProviderId ||
    new Set(catalog.presetValues.map((value) => value.presetValueId)).size !==
      catalog.presetValues.length
  ) {
    return null;
  }

  return Object.freeze({
    kind: "gift-cards",
    locationId: venue.id,
    href,
    catalogVersion: catalog.catalogVersion,
    giftCardProgramVersion: catalog.giftCardProgramVersion,
    checkoutProviderId: catalog.publication.checkoutProviderId,
    paymentProviderId: catalog.publication.paymentProviderId,
    purchaseProviderId: catalog.publication.purchaseProviderId,
    catalogItemIds: Object.freeze(
      catalog.presetValues.map((value) => value.presetValueId),
    ),
    customValueEnabled: catalog.customValue.enabled,
  });
}

export function resolveLocationProfileCommerceEntries(
  venue: VenueResult,
  workplace: SpecialistWorkplace | undefined,
  bookableCatalog: LocationBookableCatalogV1 | null,
): readonly LocationProfileCommerceEntry[] {
  const entries = [
    resolveMembershipCommerceEntry(venue, workplace),
    resolvePackageCommerceEntry(venue, bookableCatalog),
    resolveGiftCardCommerceEntry(venue, workplace),
  ].filter((entry): entry is LocationProfileCommerceEntry => entry !== null);

  return Object.freeze(entries);
}

function cloneVenueImages(images: readonly MarketplaceVenueImage[]) {
  return Object.freeze(images.map((image) => Object.freeze({ ...image })));
}

export function resolveLocationProfileSnapshot(
  locationId: string,
): LocationProfileSnapshotV2 | null {
  const normalizedLocationId = locationId.trim();
  if (!normalizedLocationId || normalizedLocationId !== locationId) {
    return null;
  }

  const venue = VENUE_RESULTS_FIXTURES.find(
    (candidate) => candidate.id === normalizedLocationId,
  );
  if (!venue) return null;

  const workplace = getSpecialistWorkplace(venue.id);
  const team = cloneDeepFrozen(resolveLocationTeam(venue, workplace));
  const bookableCatalog = resolveLocationBookableCatalog(venue, team);
  const businessEvidence = resolveLocationProfileBusinessEvidence(venue);
  const reviewEvidence = resolveLocationProfileReviewEvidence(venue);
  const portfolioEvidence = resolveLocationProfilePortfolioEvidence(
    venue,
    team,
    bookableCatalog,
  );
  const commerceEntries = resolveLocationProfileCommerceEntries(
    venue,
    workplace,
    bookableCatalog,
  );
  const nearbyLocationEvidence =
    resolveLocationProfileNearbyLocationEvidence(venue);

  return Object.freeze({
    schemaVersion: LOCATION_PROFILE_SNAPSHOT_SCHEMA_VERSION,
    providerId: LOCATION_PROFILE_PROVIDER_ID,
    providerRevision: LOCATION_PROFILE_SNAPSHOT_PROVIDER_REVISION,
    state: "published",
    locationId: venue.id,
    canonicalHref: buildLocationProfilePath(venue.id),
    name: venue.name,
    address: venue.address,
    category: venue.category,
    imageSrc: venue.imageSrc,
    imageAlt: venue.imageAlt,
    images: cloneVenueImages(venue.images),
    rating: venue.rating,
    reviewCount: venue.reviewCount,
    badge: venue.badge ? Object.freeze({ ...venue.badge }) : null,
    coordinates: Object.freeze({ ...venue.coordinates }),
    minimumPrice: venue.minimumPrice
      ? Object.freeze({ ...venue.minimumPrice })
      : null,
    acceptsGroups: venue.acceptsGroups,
    audience: Object.freeze([...venue.audience]),
    amenities: Object.freeze([...venue.amenities]),
    businessEvidenceProvider: businessEvidence?.provider ?? null,
    timeZone: businessEvidence?.timeZone ?? null,
    about: businessEvidence?.about ?? null,
    hours: businessEvidence?.hours ?? null,
    additionalInformation:
      businessEvidence?.additionalInformation ?? Object.freeze([]),
    contact: businessEvidence?.contact ?? null,
    directionsHref: businessEvidence?.directionsHref ?? null,
    reviewEvidenceProvider: reviewEvidence?.provider ?? null,
    reviews: reviewEvidence?.reviews ?? Object.freeze([]),
    team,
    portfolioEvidenceProvider: portfolioEvidence?.provider ?? null,
    portfolioItems: portfolioEvidence?.items ?? Object.freeze([]),
    bookableCatalog,
    commerceEntries,
    nearbyLocationsEvidenceProvider: nearbyLocationEvidence?.provider ?? null,
    nearbyLocations: nearbyLocationEvidence?.locations ?? Object.freeze([]),
  });
}

export function getLocationProfileServicePreview(
  snapshot: LocationProfileSnapshotV2,
) {
  return (
    snapshot.bookableCatalog?.serviceEntries.slice(
      0,
      LOCATION_PROFILE_SERVICE_PREVIEW_LIMITS.responsive,
    ) ?? []
  );
}
